import type { ReplaySubject } from 'rxjs';

export type OutputChannelTags = 'all';

/**
 * Shape of events being sent into the datalayer.
 */
export type DatalayerInputEvent<TEvent = string> = {
  event: TEvent;
  info: Record<string, any>;
};

/**
 * Shape of GDDL events.
 */
export type GDDLEvent<TEvent = string> = DatalayerInputEvent<TEvent> & {
  [key: string]: Record<string, any> | string | number;
};
/**
 * Stream used for page tracking events.
 * Accepts page urls, e.g. `/page?param=true#tracked
 */
export type PageTrackStream = ReplaySubject<string>;
/**
 * Stream used for event tracking events.
 * Accepts datalayer input events.
 */
export type EventTrackStream = ReplaySubject<DatalayerInputEvent>;

export type TaggedGDDLEvent = GDDLEvent & { tags: OutputChannelTags[] };

/**
 * Interface containing the shape of variables stored in the Datalayer.
 *
 * Every team can add their variable shapes to this interface, keeping the usage of the variables typesafe.
 *
 * Please consider that these types should be leading and imports into the datalayer models should be evaded, so using
 * external types as the shape of your datalayer variable is discouraged. Instead, define the shape of your variables
 * here and expose them to the consuming libraries if needed to keep the dataflow unidirectional.
 */
export interface DatalayerVariables {
  activeABTests: { scope: string; variant: string }[];
}

export interface Datalayer {
  init(): void;
  pushEvent(event: DatalayerInputEvent): void;
  pushPageView(path: string): void;
  pushException(event: DatalayerInputEvent): void;
  pushVariable<K extends keyof DatalayerVariables>(
    key: K,
    value: DatalayerVariables[K],
    options: { merge?: boolean }
  ): void;
  getVariable<K extends keyof DatalayerVariables>(
    key: K
  ): Partial<DatalayerVariables>[K];
  getVariables(): Partial<DatalayerVariables>;
}

export interface DatalayerOutput {
  /**
   * Identifier of the output channel.
   * Useful for event dispatch filtering.
   */
  readonly id: string;

  /**
   * Dispatches an event to the output channel.
   * Can be implemented as either a synchronous or asynchronous operation.
   *
   * @param event - The event to be dispatched.
   */
  dispatchEvent(event: GDDLEvent): void | Promise<void>;

  /**
   * Function that contains logic determining whether or not the conditions for this
   * output to be enabled are met.
   *
   * Think of things like:
   * - A certain environment variable is set to a certain value.
   * - We are not in dev mode.
   * - The platform we run on is either browser or server.
   *
   * @important These conditions are checked at runtime in order to be able to route
   * events realtime (think of e.g. changes in user consent).
   * Please consider the complexity and impact of the code you put in this method.
   */
  outputConditionsAreMet(): boolean;

  /**
   * Any initialization logic for the output can be done in this method, think of:
   * - loading scripts into the DOM.
   * - waiting for a callback from the output channel before processing events.
   * - setting up buffers.
   */
  init(): void;
}

export interface DatalayerStreamSideEffect {
  /**
   * Processing logic of the side effect.
   * @param event GDDL Event
   * @param datalayer reference to the datalayer
   */
  process: (event: GDDLEvent, datalayer: Datalayer) => void;
}

/**
 * Base class for PageTrack analytic enhancers, extends this class to implement an enhancer for page events
 */
export abstract class PageTrackEnhancer {
  abstract enhance(
    page: PageInfo,
    datalayer: Datalayer
  ): Partial<PageInfo> | Promise<Partial<PageInfo>>;
}

/**
 * Base class for EventTrack analytic enhancers, extends this class to implement an enhancer for events
 */
export abstract class EventTrackEnhancer {
  abstract enhance(
    event: DatalayerInputEvent,
    datalayer: Datalayer
  ): DatalayerInputEvent | Promise<DatalayerInputEvent>;
}

export type AnalyticsEnhancer = PageTrackEnhancer | EventTrackEnhancer;

export interface PageInfo {
  [key: string]: unknown;
  consent: number;
  url: string;
  domain: string;
  referrer: string;
  title: string;
  userAgent: string;
  path: string;
  params: Record<string, any>;
  timestamp: string;
  name: string;
  category: string;
  environment: string;
}

/**
 * Type expressing the signature of a field's link to Analytics in the CMS.
 * Added to not have a dependency on the @core/jss library here.
 *
 * The intention is to get rid of this tight coupling ASAP and use the `name` fields sent in events
 * for inference of placement of elements.
 */
export type AnalyticsRendering = {
  componentName?: string;
  fields?: { componentName?: { value: string } };
};
